import { useEffect } from 'react';

/**
 * Expands the Mini App to its full stable height on mount and keeps a few
 * CSS vars in sync with Telegram's viewport, so the page doesn't have to
 * hard-code `pb-24` for the (native, outside-the-webview) MainButton:
 *  - `--tg-viewport-stable-height`: the current stable viewport height (px).
 *  - `--tg-safe-bottom`: device safe-area inset plus Telegram's own content
 *    safe-area inset at the bottom (px), combined into one value so callers
 *    only need `calc(env(safe-area-inset-bottom) + var(--tg-safe-bottom, 0px) + …)`.
 */
export function useTelegramViewport(): void {
  useEffect(() => {
    const tg = window.Telegram?.WebApp;
    if (!tg) return;

    tg.expand?.();

    const root = document.documentElement;

    const applyViewport = () => {
      if (typeof tg.viewportStableHeight === 'number') {
        root.style.setProperty('--tg-viewport-stable-height', `${tg.viewportStableHeight}px`);
      }

      const safeBottom = tg.safeAreaInset?.bottom ?? 0;
      const contentSafeBottom = tg.contentSafeAreaInset?.bottom ?? 0;
      root.style.setProperty('--tg-safe-bottom', `${safeBottom + contentSafeBottom}px`);
    };

    applyViewport();

    tg.onEvent?.('viewportChanged', applyViewport);
    return () => {
      tg.offEvent?.('viewportChanged', applyViewport);
    };
  }, []);
}
